/**
 * Shared display formatters.
 *
 * These existed as five independent implementations across the UI, with three
 * different "just now" cutoffs (5s, 1s, 60s) and two names for the same function.
 * `shared/` had timing.js but no home for formatting, so each author paid the cost
 * again. Polish 2 phase CK-1.
 *
 * Pure functions, no DOM and no platform imports, so they are safe in shared code.
 */

/** Values that are not a usable epoch/duration all render as the same placeholder. */
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * Human-readable age of an epoch-millisecond timestamp.
 *
 * The `granularity` option reconciles the cutoffs that used to differ per call site:
 *   - 'minutes' (default) — anything under a minute is "just now". Right for lists
 *     where second-by-second precision is noise (project lists, audit timestamps).
 *   - 'seconds' — under 5s is "just now", then "12s ago". Right where recency itself
 *     is the signal (edit history, autosave).
 *
 * Beyond 7 days it falls through to `formatDate`, because "23d ago" is less useful
 * than a date.
 *
 * @param {number} ms epoch milliseconds
 * @param {Object} [opts]
 * @param {number} [opts.now=Date.now()] injectable clock, so tests need no fake timers
 * @param {'minutes'|'seconds'} [opts.granularity='minutes']
 * @param {string} [opts.fallback=''] returned when `ms` is not a finite number
 * @returns {string}
 */
export function relativeTime(ms, opts = {}) {
  const { now = Date.now(), granularity = 'minutes', fallback = '' } = opts;
  if (!isFiniteNumber(ms)) return fallback;

  const elapsed = Math.max(0, now - ms);
  const seconds = Math.round(elapsed / 1000);

  if (granularity === 'seconds') {
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
  } else if (seconds < 60) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(ms);
}

/**
 * Locale date-time string, guarded — `toLocaleString` can throw on exotic values.
 * @param {number} ms epoch milliseconds
 * @param {string} [fallback='—']
 * @returns {string}
 */
export function formatDate(ms, fallback = '—') {
  if (!isFiniteNumber(ms)) return fallback;
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

/**
 * Clock time within a day — '2:36 PM'. For a list that is already grouped by
 * day, where the row only has to carry the time within its group.
 *
 * Deliberately NOT relative: "3h ago" and "4h ago" are one hour apart only if
 * you read them in the same second, and on a surface whose whole task is
 * comparing rows to each other that is the wrong axis. `relativeTime` above is
 * right when recency itself is the signal; this is right when ORDER is.
 *
 * DUPLICATE TO COLLAPSE: `ui/history-rail/version-model.js` exports the same
 * function as `clockTime`, and its docstring carries the reasoning above. It is
 * not re-exported from there because a shared formatter must not depend on a
 * feature directory that is written to be deleted — `history-rail-revert-gate.js`
 * refuses an undeclared dependency on it, correctly. Collapse the rail's copy
 * onto this one next time that file is opened.
 *
 * @param {number} ms epoch milliseconds
 * @param {string} [fallback='—']
 * @returns {string}
 */
export function formatTime(ms, fallback = '—') {
  if (!isFiniteNumber(ms)) return fallback;
  try {
    return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return fallback;
  }
}

/**
 * Byte count as B / KB / MB. Matches the implementation this replaces, so existing
 * copy and test expectations are unchanged.
 * @param {number} bytes
 * @returns {string} '' when not a finite number
 */
export function formatBytes(bytes) {
  if (!isFiniteNumber(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Elapsed duration for tickers and activity chips: '820ms', '12s', '1m 12s', '2h 5m'.
 * @param {number} ms
 * @returns {string} '' when not a finite number
 */
export function formatDuration(ms) {
  if (!isFiniteNumber(ms) || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;
}

/** A display count with its noun, including irregular plurals when supplied. */
export function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}
